import React, { useState } from "react";
import { Box, Button, Grid, Typography, TextField, MenuItem, Paper, CircularProgress } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { generateSingleInventoryTag, GenerateSingleInventoryTagRequest } from "../../api/inventoryTags/index.ts";

const validationSchema = Yup.object().shape({
  companyId: Yup.number()
    .required("El ID de empresa es requerido")
    .min(1, "El ID de empresa debe ser mayor a 0"),
  productCode: Yup.string(),
  description: Yup.string(),
  unit: Yup.string(),
  warehouseName: Yup.string(),
  countType: Yup.string()
    .oneOf(["PRIMER", "SEGUNDO"], "Debe ser PRIMER o SEGUNDO")
    .required("El tipo de conteo es requerido"),
  consecutiveCode: Yup.string(),
});

const InventoryTagTestForm: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: GenerateSingleInventoryTagRequest) => {
    setSubmitting(true);
    try {
      const response = await generateSingleInventoryTag(values);

      // Handle PDF download
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "inventory_tag.pdf";

      if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      openSnack("Marbete generado correctamente", "success");
    } catch (error: any) {
      console.error("Error generating inventory tag:", error);
      const errorMessage = error.response?.data?.message || "Error al generar el marbete";
      openSnack(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Probar Marbete Individual de Inventario
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complete los campos para generar un marbete de inventario individual de prueba.
        </Typography>

        <Formik
          initialValues={{
            companyId: selectedCompany?.id || 1,
            productCode: "TEST-001",
            description: "Producto de prueba para marbete",
            unit: "KILOGRAMO(S)",
            warehouseName: "Almacén Principal",
            countType: "PRIMER",
            consecutiveCode: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FastField
                    name="companyId"
                    as={TextField}
                    label="ID de Empresa"
                    type="number"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 1 }}
                    error={touched.companyId && Boolean(errors.companyId)}
                    helperText={touched.companyId && errors.companyId}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FastField
                    name="productCode"
                    as={TextField}
                    label="Código de Producto"
                    variant="outlined"
                    fullWidth
                    error={touched.productCode && Boolean(errors.productCode)}
                    helperText={touched.productCode && errors.productCode}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FastField
                    name="description"
                    as={TextField}
                    label="Descripción"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FastField
                    name="unit"
                    as={TextField}
                    label="Unidad"
                    variant="outlined"
                    fullWidth
                    error={touched.unit && Boolean(errors.unit)}
                    helperText={touched.unit && errors.unit}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FastField
                    name="warehouseName"
                    as={TextField}
                    label="Nombre de Almacén"
                    variant="outlined"
                    fullWidth
                    error={touched.warehouseName && Boolean(errors.warehouseName)}
                    helperText={touched.warehouseName && errors.warehouseName}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FastField
                    name="countType"
                    as={TextField}
                    label="Tipo de Conteo"
                    variant="outlined"
                    fullWidth
                    select
                    error={touched.countType && Boolean(errors.countType)}
                    helperText={touched.countType && errors.countType}
                  >
                    <MenuItem value="PRIMER">PRIMER CONTEO</MenuItem>
                    <MenuItem value="SEGUNDO">SEGUNDO CONTEO</MenuItem>
                  </FastField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FastField
                    name="consecutiveCode"
                    as={TextField}
                    label="Código Consecutivo (opcional)"
                    variant="outlined"
                    fullWidth
                    placeholder="Ej: 24-0001"
                    error={touched.consecutiveCode && Boolean(errors.consecutiveCode)}
                    helperText={touched.consecutiveCode ? errors.consecutiveCode : "Si se deja vacío, se generará automáticamente"}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      sx={{ minWidth: 150 }}
                    >
                      {submitting ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Generando...
                        </>
                      ) : (
                        "Generar Marbete"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default InventoryTagTestForm;

